import client from "@/src/apollo/client";
import ArticleContent from "@/src/components/Article/Content";
import Header from "@/src/components/Article/Header";
import Paywalled from "@/src/components/Article/Paywalled";
import React from "react";
import GenericHead from "@/src/components/Head/GenericHead";
import Loading from "@/src/components/Loading";
import { SRIPE_PACKAGE_ID_TO_SPORT } from "@/src/components/Stripe/constants";
import { PREMIUM_CATEGORY, WP_URL } from "@/src/constants";
import {
  GET_POSTS,
  GET_POST_PREVIEW,
  GET_POST_PREVIEW_SLUG,
} from "@/src/gql/queries/posts";
import type { GetStaticProps } from "next";
import Image from "next/image";
import { useRouter } from "next/router";
import Script from "next/script";
import { useEffect, useState } from "react";
import ArticleHead, {
  getSportFromCategories,
} from "@/src/components/Head/ArticleHead";

export interface PreviewPostProps {
  data: any;
  isLoggedIn: boolean;
  isPaywall: boolean;
  subs?: string[];
}

interface CanAccessArticleProps {
  categories: string[];
  subscriptions: string[];
  products: string[];
}

const PreviewPost = ({
  data,
  isLoggedIn,
  isPaywall,
  subs = [],
  ...rest
}: PreviewPostProps) => {
  const router = useRouter();

  const [showPaywall, setShowPaywall] = useState(false);

  const loading = true;
  let postBGImage = "";

  if (data?.post?.featuredImage?.node?.mediaDetails?.sizes) {
    postBGImage =
      data?.post?.featuredImage?.node?.mediaDetails?.sizes[0]?.sourceUrl;
  } else if (data?.post?.featuredImage?.node?.uri) {
    postBGImage = `${WP_URL}${data?.post?.featuredImage?.node?.uri}`;
  }

  const author = data?.post?.author?.node;
  const categories = data?.post?.categories?.nodes;
  const subtitle = data?.post?.articleOptions?.byline;
  const publishedDate = data?.post?.date;
  const contributors = data?.post?.contributors?.nodes || [];
  let relatedPosts: any = [];

  if (contributors.length > 0) {
    if (contributors.length === 1) {
      relatedPosts = [
        ...contributors[0]?.posts?.edges.map(function (m: any) {
          return m.node;
        }),
      ];
    } else {
      contributors?.map(function (contributor: any) {
        relatedPosts.push(
          ...contributor?.posts?.edges.map(function (edge: any) {
            return edge.node;
          })
        );
      });
    }
  }

  relatedPosts = relatedPosts.filter(
    (post: any) => post.uri !== data?.post?.uri
  );

  useEffect(() => {
    let isPremium = false;
    categories?.map(function (category: object) {
      //@ts-ignore
      if (category.slug === PREMIUM_CATEGORY) {
        isPremium = true;
      }
    });

    // for each of the subs map to package id
    const packageSubs: string[] = [];

    subs.forEach((sub) => {
      if (sub in SRIPE_PACKAGE_ID_TO_SPORT) {
        packageSubs.push(SRIPE_PACKAGE_ID_TO_SPORT[sub]);
      }
    });
    let canAccess = false;
    // if full in subs - show
    if (packageSubs.includes("FULL")) {
      canAccess = true;
    } else {
      //@ts-ignore
      categories?.map(function (category) {
        if (packageSubs.includes(category.name)) {
          canAccess = true;
        }
      });
    }

    if (isPremium && !canAccess) {
      setShowPaywall(true);
    } else {
      setShowPaywall(false);
    }
  }, [data, isLoggedIn, categories]);

  return (
    <>
      <Loading isLoading={router.isFallback} />
      <GenericHead
        title={data?.post?.title}
        description={data?.post?.articleOptions?.byline}
        image={postBGImage}
      />
      <ArticleHead
        article={{
          title: data?.post?.title,
          description: data?.post?.articleOptions?.byline,
          imageUrl: postBGImage,
          author: author?.name,
          authorSlug: author?.slug,
          //@ts-expect-error dumb
          category: categories?.map((cat) => cat.name),
          //@ts-expect-error dumb
          tags: categories?.map((cat) => cat.name) ?? [],
          sport: getSportFromCategories(
            categories?.map((cat: any) => cat.name) ?? []
          ),
          publishDate: publishedDate,
          slug: data?.post?.uri,
          modified: data?.post?.modified,
        }}
      />
      <Script src="https://embed-cdn.gettyimages.com/widgets.js" />
      <Script src="https://components.racingandsports.com/iframejs/ras.sports.js" />

      {/* MUX */}
      <link
        rel="stylesheet"
        id="mux-video-gutenberg-block-styles-css"
        href="https://blog.scplaybook.com.au/wp-content/plugins/2coders-integration-mux-video/assets/css/admin.css?ver=1752363787"
        type="text/css"
        media="all"
      />
      <Script
        type="text/javascript"
        src="https://cdn.jsdelivr.net/npm/@mux/mux-player"
      />
      <div className="bg-white">
        {postBGImage !== "" && (
          <div
            className={
              "w-[100%] min-h-30 bg-cover bg-top bg-center article-image relative"
            }
          >
            <Image
              src={postBGImage}
              fill
              objectFit="cover"
              objectPosition="center"
              alt={`Image for article - ${data?.post?.title}`}
              priority
            />
          </div>
        )}
        <Header
          title={data?.post?.title}
          subTitle={subtitle}
          categories={categories}
          author={author}
          date={publishedDate}
          modified={data?.post?.modified}
          contributors={contributors}
        />

        {!showPaywall ? (
          <>
            <ArticleContent
              postContent={data?.post?.content}
              relatedPosts={relatedPosts}
              comments={data?.post?.comments?.nodes ?? []}
              postId={data?.post?.postId}
            />
          </>
        ) : (
          <Paywalled excerpt={data?.post?.excerpt} />
        )}
      </div>
    </>
  );
};
export async function getStaticPaths() {
  const { data, errors } = await client.query({
    query: GET_POSTS,
    variables: {
      last: 25,
    },
  });
  //@ts-ignore
  const pathsData = [];

  data?.posts?.edges &&
    data?.posts?.edges.map((page: any) => {
      //const slugs = page?.node?.uri?.join( '/' ).filter( (pageSlug:any) => pageSlug );

      const slugs = page?.node?.uri?.split("/");
      slugs.shift();
      slugs.pop();

      pathsData.push({ params: { slug: slugs } });
    });
  //@ts-ignore
  return {
    //@ts-ignore
    paths: pathsData,
    fallback: true,
  };
}

export const getStaticProps: GetStaticProps = async (context) => {
  let post,
    postErrors = null;
  const { data, errors } = await client.query({
    query: GET_POST_PREVIEW,
    variables: {
      //@ts-ignore
      uri: `/${context.params?.slug?.join("/") ?? "/"}`,
    },
  });

  if (!data.post) {
    const lastSlug = context?.params?.slug;
    const { data, errors } = await client.query({
      query: GET_POST_PREVIEW_SLUG,
      variables: {
        //@ts-ignore
        slug: `${lastSlug[lastSlug.length - 1]}}`,
      },
    });
    if (data.post) {
      return {
        redirect: {
          destination: data.post.uri,
          permanent: true,
        },
      };
    }
  }

  return {
    props: {
      errors: errors ?? null,
      data: data ?? null,
    },
  };
};

export default PreviewPost;
